import Protomux from 'protomux'
import c from 'compact-encoding'
import b4a from 'b4a'
import z32 from 'z32'
import { domainHash, sign, verify } from '@pine/crypto'
import { now as clockNow, toDevice } from './clock.js'

const PROTOCOL = 'pine/room/1'

/**
 * Live, unstored signalling for one room: typing indicators, presence, read
 * receipts and WebRTC call setup.
 *
 * These never touch a Hypercore. They ride the same Noise streams the room
 * already replicates over, on a Protomux channel keyed by the room's discovery
 * key, so a peer who is not in the room never sees the channel exist.
 *
 * Every frame is signed with the sender's account key. The transport already
 * authenticates the *peer*, but a peer can be in the room under a different
 * account than the one it claims in a call invite, so the signature is what
 * actually stops caller-ID spoofing.
 */

const envelopeEncoding = {
  preencode (state, m) {
    c.uint.preencode(state, m.type)
    c.fixed32.preencode(state, m.account)
    c.uint.preencode(state, m.at)
    c.buffer.preencode(state, m.body)
    c.fixed64.preencode(state, m.signature)
  },
  encode (state, m) {
    c.uint.encode(state, m.type)
    c.fixed32.encode(state, m.account)
    c.uint.encode(state, m.at)
    c.buffer.encode(state, m.body)
    c.fixed64.encode(state, m.signature)
  },
  decode (state) {
    return {
      type: c.uint.decode(state),
      account: c.fixed32.decode(state),
      at: c.uint.decode(state),
      body: c.buffer.decode(state),
      signature: c.fixed64.decode(state)
    }
  }
}

export const EPH = {
  TYPING: 0,
  PRESENCE: 1,
  RECEIPT: 2,
  CALL: 3,
  PROFILE: 4,
  // "Let this device of mine write": see Room.seekAdmission.
  ADMIT: 5
}

/**
 * Anything older than this is treated as a replay and dropped.
 *
 * Older by the server's clock (clock.js), on both ends. Frames used to be
 * stamped and judged by each phone's own clock, and a phone set a couple of
 * minutes wrong (automatic time switched off is enough) dropped every frame
 * from everybody else as a replay, and everybody else dropped its frames:
 * no typing, no presence, and a call that rang on one side only, or never
 * connected. Messages had been moved to the server's clock in 0.2.3 for
 * the same reason; this channel was left on the phone's.
 */
const MAX_AGE = 60 * 1000

export class EphemeralBus {
  constructor ({ identity, topic, onMessage, onPeer = null }) {
    this.identity = identity
    this.topic = topic
    this.onMessage = onMessage
    this.onPeer = onPeer
    this.channels = new Map() // remotePublicKey hex -> { channel, message }
    this.muxes = new Set()
  }

  /**
   * Attach to a swarm connection — freshly opened or already established.
   *
   * The peer is named by the connection's own remote key. It used to be taken
   * from the second argument, which for connections that already existed when
   * the room opened was the connection itself — and a connection's
   * `publicKey` is *our* key, not theirs. Every one of those connections was
   * filed under the same name, so only the first got a bus: a chat opened
   * while connected to several people carried typing, presence and the ring
   * of a call to one of them, whichever happened to come first.
   */
  attach (stream, info) {
    const key = stream.remotePublicKey || (info && info.publicKey)
    if (!key) return
    const remoteId = b4a.toString(key, 'hex')
    const current = this.channels.get(remoteId)
    if (current && current.stream === stream) return

    const mux = Protomux.from(stream)
    this.muxes.add(mux)

    // Register the pairing hook *before* opening our own channel. Protomux
    // rejects a channel opened for a protocol the other side has not created
    // yet and never replays it — so between two peers already connected for
    // replication (which is every peer who shares this room), whichever side
    // opened first was refused and the bus never formed. That is what stopped
    // typing, presence and — the visible one — call signalling from arriving.
    mux.pair({ protocol: PROTOCOL, id: this.topic }, () => this._open(mux, stream, remoteId))
    this._open(mux, stream, remoteId)
  }

  _open (mux, stream, remoteId) {
    const current = this.channels.get(remoteId)
    if (current && current.stream === stream) return

    const channel = mux.createChannel({
      protocol: PROTOCOL,
      id: this.topic,
      // Both ends have the channel: say hello at once rather than at the
      // next presence tick, up to twenty seconds away. Whoever is deciding
      // whether to wake us is looking at exactly this.
      onopen: () => { if (this.onPeer) this.onPeer(remoteId) },
      onclose: () => this._forget(remoteId, channel)
    })
    if (!channel) return // already open on this connection

    const message = channel.addMessage({
      encoding: envelopeEncoding,
      onmessage: (envelope) => this._receive(envelope, remoteId)
    })

    channel.open()

    /*
     * A second connection to the same peer replaces the first.
     *
     * Hyperswarm only ever hands over one live connection per peer, so a new
     * one means the old one is on its way out — or, after a dropped link, is
     * already dead and simply was never told. Keeping the old channel is what
     * left a chat's live signals going into a connection nobody was at.
     */
    if (current) {
      try { current.channel.close() } catch {}
    }
    this.channels.set(remoteId, { channel, message, stream })

    stream.once('close', () => this._forget(remoteId, channel))
  }

  /** Drop a peer's entry, unless a newer connection has already taken its place. */
  _forget (remoteId, channel) {
    const current = this.channels.get(remoteId)
    if (current && current.channel === channel) this.channels.delete(remoteId)
  }

  _receive (envelope, remoteId) {
    if (!envelope || !envelope.signature) return
    const age = Math.abs(clockNow() - envelope.at)
    if (age > MAX_AGE) return

    const payload = signable(this.topic, envelope)
    if (!verify(payload, envelope.signature, envelope.account)) return

    let body = null
    try {
      body = JSON.parse(b4a.toString(envelope.body, 'utf8'))
    } catch {
      return
    }

    this.onMessage({
      type: envelope.type,
      account: z32.encode(envelope.account),
      accountKey: envelope.account,
      // Back on this phone's clock, which is what presence and "last seen"
      // are measured against here.
      at: toDevice(envelope.at),
      body,
      from: remoteId
    })
  }

  /** Broadcast to everyone in the room, or to one peer if `to` is given. */
  send (type, body, { to = null } = {}) {
    const envelope = {
      type,
      account: this.identity.publicKey,
      at: clockNow(),
      body: b4a.from(JSON.stringify(body ?? {}), 'utf8'),
      signature: null
    }
    envelope.signature = sign(signable(this.topic, envelope), this.identity.secretKey)

    if (to) {
      const entry = this.channels.get(to)
      if (entry) safeSend(entry.message, envelope)
      return
    }
    for (const entry of this.channels.values()) safeSend(entry.message, envelope)
  }

  get peerCount () {
    return this.channels.size
  }

  destroy () {
    for (const mux of this.muxes) {
      try { mux.unpair({ protocol: PROTOCOL, id: this.topic }) } catch {}
    }
    this.muxes.clear()
    for (const { channel } of this.channels.values()) {
      try { channel.close() } catch {}
    }
    this.channels.clear()
  }
}

function safeSend (message, envelope) {
  try { message.send(envelope) } catch { /* peer went away mid-send */ }
}

function signable (topic, envelope) {
  return domainHash(
    'pine/eph/v1',
    topic,
    String(envelope.type),
    envelope.account,
    String(envelope.at),
    envelope.body
  )
}
